/**
 * Profile-scoped local drafts. Completion IDs stay in LearnProgress.
 * Every localStorage touch is guarded: Safari private mode throws on
 * setItem, quota errors throw, and some embedded webviews throw on the
 * accessor itself. A lesson must never crash because a draft could not be
 * remembered.
 */

const profileSuffix = (profileId) => (profileId ? String(profileId).toLowerCase() : "guest");

const key = (base, profileId, extra = "") =>
  extra ? `${base}.${profileSuffix(profileId)}.${extra}` : `${base}.${profileSuffix(profileId)}`;

const readRaw = (storageKey) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

const writeRaw = (storageKey, value) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    window.localStorage.setItem(storageKey, value);
    return true;
  } catch {
    return false; // private mode / quota — drafts are best-effort
  }
};

const readJSON = (storageKey, fallback) => {
  const raw = readRaw(storageKey);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const writeJSON = (storageKey, value) => {
  try {
    return writeRaw(storageKey, JSON.stringify(value));
  } catch {
    return false;
  }
};

export const writingLocalStore = {
  lastScore(base, profileId) {
    const n = Number(readRaw(key(base, profileId)) || 0);
    return Number.isFinite(n) ? n : 0;
  },
  recordScore(base, profileId, score) {
    writeRaw(key(base, profileId), String(Math.max(0, Number(score) || 0)));
  },
  /** Keep the best of previous + new; returns the stored best. */
  recordBestScore(base, profileId, score) {
    const best = Math.max(writingLocalStore.lastScore(base, profileId), Number(score) || 0);
    writingLocalStore.recordScore(base, profileId, best);
    return best;
  },
  lesson10Sentences(profileId) {
    const saved = readJSON(key("pixley.learn.writing.lesson10.chosenSentences.v1", profileId), []);
    return Array.isArray(saved) ? saved : [];
  },
  saveLesson10Sentence(profileId, sentence, round) {
    const saved = writingLocalStore.lesson10Sentences(profileId);
    while (saved.length <= round) saved.push("");
    saved[round] = sentence;
    writeJSON(key("pixley.learn.writing.lesson10.chosenSentences.v1", profileId), saved);
  },
  lesson10PictureSaved(profileId) {
    return readRaw(key("pixley.learn.writing.lesson10.pictureSentence.saved.v1", profileId)) === "1";
  },
  saveLesson10Picture(profileId) {
    writeRaw(key("pixley.learn.writing.lesson10.pictureSentence.saved.v1", profileId), "1");
  },
  lesson11Story(profileId) {
    const fallback = { lines: {}, chosenEnding: "", savedToMyBook: false };
    const saved = readJSON(key("pixley.learn.writing.lesson11.myStory.v1", profileId), fallback);
    return saved && typeof saved === "object" ? { ...fallback, ...saved } : fallback;
  },
  saveLesson11Story(profileId, state) {
    writeJSON(key("pixley.learn.writing.lesson11.myStory.v1", profileId), state);
  },
  lesson12Story(profileId) {
    const fallback = {
      organizer: {},
      goal: 0,
      draftedSentenceCount: 0,
      draftedParts: [],
      revisionChangeMade: false,
      grownSentence: false,
      savedToMyBook: false,
      replyWritten: false,
      draftSentences: [],
    };
    const saved = readJSON(key("pixley.learn.writing.lesson12.storyState.v1", profileId), fallback);
    return saved && typeof saved === "object" ? { ...fallback, ...saved } : fallback;
  },
  saveLesson12Story(profileId, state) {
    writeJSON(key("pixley.learn.writing.lesson12.storyState.v1", profileId), state);
  },
};

export default writingLocalStore;
